import React, { useState, useEffect, useMemo } from 'react';
import {
    Paper,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Button
} from '@mui/material';
import { useWorkplaceUser } from 'module/documents/utils';
import { createChecklist, deleteChecklist, updateChecklist, createChecklistTemplete, getRolesAssignedToChecklist, createChecklistGroup, deleteChecklistGroup, GetFrequencyList, getAllChecklistCategories, getAllChecklistGroups, getChecklistTemplates, updateChecklistTemplate, deleteChecklistTemplate, getUsersByWorkplace } from 'core/API/checklist';
import styles from './ChecklistSetting.module.scss';
import { CheckListAndToDoGroups, ChecklistAssignmentGroup as ChecklistGroup } from './CheckListAndToDoGroups';
import { ChecklistCategory } from './ChecklistCategory';
import { DeleteDialog } from './Dialog';
import { ChecklistTemplates } from './ChecklistTemplates';
import { AddChecklist } from './AddChecklist';
import { AssignmentGroupForm, AssignmentGroupFormValues, UserOption } from './AssignmentGroupForm';
import { mapChecklistAssignmentGroups } from './mapChecklistAssignmentGroups';
import { useAuth } from 'module/auth/context/AuthContext';
import { usePermissionCheck } from 'core/hooks/usePermissionCheck';

// Utility function to get workplace ID from localStorage
const getWorkplaceId = (): number => {
    const workplaceId = localStorage.getItem("workplace-id");
    return workplaceId ? Number(workplaceId) : 1; // Default to 1 if not found
};

// Define interfaces
interface Checklist {
    id: number;
    label: string;
    category: string;
    owner: string;
    workplace?: { id: number; name: string } | null;
    organization?: { id: number; name: string } | null;
    frequency?: string; // Add frequency for prefill
    signoff_required?: boolean;
    tasks?: any[]; // Array of tasks for the checklist
    templateUid?: string | null; // Template UID from API
    version?: number; // Version number
    isScheduled?: boolean; // Whether template is scheduled
    type?: string | null;
}

// Using AssignmentGroup type exported from CheckListAndToDoGroups

// Main ChecklistSetting Component
const ChecklistSetting = () => {
    const auth = useAuth();
    const { hasViewPermission, hasEditPermission, hasDeletePermission, hasExecutePermission } = usePermissionCheck();
    const canViewChecklist = hasViewPermission('Checklist');
    const canEditChecklist = hasEditPermission('Checklist');
    const canDeleteChecklist = hasDeletePermission('Checklist');
    const canExecuteChecklist = hasExecutePermission('Checklist');
    // State for views and checklist management (unchanged)
    const [currentView, setCurrentView] = useState<'ChecklistSetting' | 'EditChecklist' | 'AddChecklist' | 'AddAssignmentGroup' | 'EditAssignmentGroup'>('ChecklistSetting');
    const [editingChecklist, setEditingChecklist] = useState<Checklist | null>(null);
    const [templateValue, setTemplateValue] = useState('');
    const [templateUser, setTemplateUser] = useState<string>('');
    const [categories, setCategories] = useState<string[]>([]);
    const [showInput, setShowInput] = useState(false);
    const [newCategory, setNewCategory] = useState<any>('');
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [editText, setEditText] = useState('');
    const [categorysData, setCategorysData] = useState<{ id: number; label: string }[]>([]);
    const [frequencyData, setFrequencyData] = useState<any[]>([]); // Make frequencyData dynamic and type-safe
    const [checklists, setChecklists] = useState<Checklist[]>([]);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [deleteType, setDeleteType] = useState<'category' | 'checklist' | null>(null);
    const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
    const [deleteChecklistTemplateUid, setDeleteChecklistTemplateUid] = useState<string | null>(null);
    
    // Assignment group delete dialog states
    const [assignmentGroupDeleteDialogOpen, setAssignmentGroupDeleteDialogOpen] = useState(false);
    const [assignmentGroupToDelete, setAssignmentGroupToDelete] = useState<ChecklistGroup | null>(null);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [checklistGroupUpdate, setchecklistGroupUpdate] = useState(false);
    const dataa = useWorkplaceUser();
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'info' | 'warning';
    }>({
        open: false,
        message: '',
        severity: 'info',
    });

    // Assignment groups state
    const [rawAssignmentGroups, setRawAssignmentGroups] = useState<any[]>([]);
    const [editingAssignmentGroup, setEditingAssignmentGroup] = useState<AssignmentGroupFormValues | null>(null);

    // Users list for the USERS assignment type
    const [users, setUsers] = useState<UserOption[]>([]);
    const [usersLoading, setUsersLoading] = useState(false);

    const assignmentGroups = useMemo(
        () => mapChecklistAssignmentGroups(rawAssignmentGroups, users),
        [rawAssignmentGroups, users]
    );

    // Add loading states for all three sections
    const [assignmentGroupsLoading, setAssignmentGroupsLoading] = useState(true);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [templatesLoading, setTemplatesLoading] = useState(true);

    const sortedChecklists = useMemo(() => {
        return [...checklists].sort((a, b) => a.category.localeCompare(b.category));
    }, [checklists]);


    useEffect(() => {
        // Fetch dynamic roles
        const fetchData = async () => {
            try {
                const res = await getRolesAssignedToChecklist();
                const sortedRoles = Array.isArray(res) ? 
                    [...res].sort((a, b) => a.label.localeCompare(b.label)) : 
                    [];
                setDynamicRoles(sortedRoles);
            } catch (error) {
                console.error('Error fetching roles:', error);
                setDynamicRoles([]);
            }
        };
        fetchData();

        // Fetch checklist groups from API using the API layer (recommended)
        const fetchGroups = async () => {
            try {
                setAssignmentGroupsLoading(true);
                const apiGroups = await getAllChecklistGroups();
                const groupsArray = Array.isArray(apiGroups) ? apiGroups : [];
                setRawAssignmentGroups(groupsArray);
            } catch (e) {
                console.error('Error fetching checklist groups:', e);
                setRawAssignmentGroups([]);
            } finally {
                setAssignmentGroupsLoading(false);
            }
        };
        fetchGroups();

        // Fetch users for the USERS assignment type
        const fetchUsers = async () => {
            try {
                setUsersLoading(true);
                const apiUsers = await getUsersByWorkplace();
                const usersArray = Array.isArray(apiUsers) ? apiUsers : [];
                setUsers(
                    usersArray.map((u: any): UserOption => ({
                        id: u.id,
                        firstName: u.firstName || '',
                        lastName: u.lastName || '',
                        email: u.email || '',
                    }))
                );
            } catch (e) {
                console.error('Error fetching users for assignment groups:', e);
                setUsers([]);
            } finally {
                setUsersLoading(false);
            }
        };
        fetchUsers();

        // Fetch frequency list
        const getFrequntlyData = async () => {
            try {
                const res = await GetFrequencyList();
                const arr = Array.isArray(res) ? res : Object.values(res);
                setFrequencyData(arr);
            } catch (error) {
                console.error('Error fetching frequency data:', error);
                setFrequencyData([]);
            }
        };
        getFrequntlyData();

        // Fetch checklist categories (simulate API or use correct format)
        // Example: Replace with your real API call
        const getChecklistCategories = async () => {
            try {
                setCategoriesLoading(true);
                // Simulate API response: [{ id: 1, label: 'Safety' }, ...]
                const res = await getAllChecklistCategories();
                
                // Ensure we have an array
                const categoriesArray = Array.isArray(res) ? res : [];
                
                setCategorysData(categoriesArray);
            } catch (error) {
                console.error('Error fetching checklist categories:', error);
                setCategorysData([]);
                setSnackbar({
                    open: true,
                    message: 'Failed to fetch checklist categories',
                    severity: 'error',
                });
            } finally {
                setCategoriesLoading(false);
            }
        };
        getChecklistCategories();

        // Fetch checklist templates dynamically
        const fetchChecklistTemplates = async () => {
            try {
                setTemplatesLoading(true);
                const apiChecklists = await getChecklistTemplates();
                
                // Ensure we have an array
                const checklistsArray = Array.isArray(apiChecklists) ? apiChecklists : [];
                
                // Map API response to Checklist shape for the UI
                const mappedChecklists = checklistsArray.map((item: any) => ({
                    id: item.id,
                    label: item.label || item.name || 'Unnamed Template',
                    category: item.category?.label || item.category?.name || '',
                    owner: item.author ? `${item.author.firstName || ''} ${item.author.lastName || ''}`.trim() || 'Unknown' : 'Unknown',
                    workplace: item.workplace ? { id: item.workplace.id, name: item.workplace.name } : null,
                    organization: item.organization ? { id: item.organization.id, name: item.organization.name } : null,
                    frequency: item.frequency?.label || item.frequency?.name || '',
                    signoff_required: item.signoffRequired || false,
                    tasks: item.tasks || [],
                    templateUid: item.templateUid || null,
                    version: item.version || 1,
                    isScheduled: item.isScheduled || false,
                    type: item.type || null,
                }));
                
                setChecklists(mappedChecklists);
            } catch (e) {
                console.error('Error fetching checklist templates:', e);
                setChecklists([]);
                setSnackbar({
                    open: true,
                    message: 'Failed to fetch checklist templates',
                    severity: 'error',
                });
            } finally {
                setTemplatesLoading(false);
            }
        };
        fetchChecklistTemplates();
    }, [])

    // Add dynamic roles state inside the component
    const [dynamicRoles, setDynamicRoles] = useState<{ id: number; label: string; backgroundColor: string; textColor: string }[]>([]);

    const refreshAssignmentGroups = async () => {
        try {
            setAssignmentGroupsLoading(true);
            const apiGroups = await getAllChecklistGroups();
            const groupsArray = Array.isArray(apiGroups) ? apiGroups : [];
            setRawAssignmentGroups(groupsArray);
        } catch (e) {
            console.error('Error fetching checklist groups:', e);
            setRawAssignmentGroups([]);
        } finally {
            setAssignmentGroupsLoading(false);
        }
    };

    const handleCreateClick = () => {
        if (!canExecuteChecklist) {
            return;
        }
        setShowInput(true);
        setNewCategory('');
    };

    const handleAddCategory = async () => {
        if (!canExecuteChecklist) {
            return;
        }
        if (!newCategory.trim()) return;
        // Generate a unique id for the new category
        const newId = categorysData.length > 0 ? Math.max(...categorysData.map(c => c.id)) + 1 : 1;
        const newCatObj = { id: newId, label: newCategory.trim() };
        setCategories([...categories, newCategory.trim()]);
        setNewCategory('');
        setShowInput(false);
        try {
            const res = await createChecklist({ label: newCategory.trim() });
            setSnackbar({
                open: true,
                message: 'Category added successfully',
                severity: 'success',
            });
            await refreshCategories();
        } catch (error) {
            setSnackbar({
                open: true,
                message: 'Failed to add category',
                severity: 'error',
            });
        }
    };

    const handleEditClick = (index: number) => {
        if (!canEditChecklist) {
            return;
        }
        setEditIndex(index);
        setEditText(categories[index]);
    };

    const handleSaveEdit = async (index: number, updatename: any) => {
        if (!canEditChecklist) {
            return;
        }
        if (editIndex === null) return;
        const updated = [...categories];
        updated[editIndex] = editText.trim();
        setCategories(updated);
        setEditIndex(null);
        setEditText('');
        try {
            // Get the actual category ID from categorysData instead of using array index
            const categoryId = categorysData[editIndex]?.id;
            if (!categoryId) {
                throw new Error('Category ID not found');
            }
            await updateChecklist(categoryId, updatename);
            setSnackbar({
                open: true,
                message: 'Category updated successfully',
                severity: 'success',
            });
            await refreshCategories();
        } catch (error) {
            setSnackbar({
                open: true,
                message: 'Failed to update category',
                severity: 'error',
            });
        }
    };

    const handleDeleteCategoryClick = (index: number) => {
        if (!canDeleteChecklist) {
            return;
        }
        setDeleteType('category');
        setDeleteIndex(index);
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteChecklistClick = (id: number) => {
        if (!canDeleteChecklist) {
            return;
        }
        setDeleteType('checklist');
        // Find the checklist by id and get its templateUid
        const checklist = checklists.find(c => c.id === id);
        if (!checklist?.templateUid) {
            setSnackbar({
                open: true,
                message: 'Cannot delete template: Template UID not found',
                severity: 'error',
            });
            return;
        }
        setDeleteChecklistTemplateUid(checklist.templateUid);
        setIsDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        try {
            if (!canDeleteChecklist) {
                return;
            }
            if (deleteType === 'category' && deleteIndex !== null) {
                const updated = categories.filter((_, i) => i !== deleteIndex);
                setCategories(updated);
                try {
                    await deleteChecklist(deleteIndex);
                    setSnackbar({
                        open: true,
                        message: 'Category deleted successfully',
                        severity: 'success',
                    });
                    await refreshCategories();
                } catch (error: any) {
                    // Extract backend error message - error is the response data due to axios interceptor
                    let errorMessage = 'Failed to delete category';
                    
                    if (error && typeof error === 'object') {
                        // The error object is actually the response data from the server
                        if (error.error) {
                            // Direct error property from server response
                            errorMessage = error.error;
                        } else if (error.message) {
                            // Generic error message
                            errorMessage = error.message;
                        } else if (error.status === 409) {
                            // Fallback for 409 without specific backend message
                            errorMessage = 'Cannot delete category: It is being used by existing checklist templates';
                        }
                    }
                    
                    setSnackbar({
                        open: true,
                        message: errorMessage,
                        severity: 'error',
                    });
                }
            } else if (deleteType === 'checklist' && deleteChecklistTemplateUid !== null) {
                try {
                    await deleteChecklistTemplate(deleteChecklistTemplateUid);
                    setSnackbar({
                        open: true,
                        message: 'Checklist template deleted successfully',
                        severity: 'success',
                    });
                    await refreshChecklistTemplates();
                } catch (error: any) {
                    // Extract backend error message - error is the response data due to axios interceptor
                    let errorMessage = 'Failed to delete checklist template';
                    
                    if (error && typeof error === 'object') {
                        // The error object is actually the response data from the server
                        if (error.error) {
                            // Direct error property from server response
                            errorMessage = error.error;
                        } else if (error.message) {
                            // Generic error message
                            errorMessage = error.message;
                        } else if (error.status === 409) {
                            // Fallback for 409 without specific backend message
                            errorMessage = 'Cannot delete this checklist template. It is associated with existing checklist schedules.';
                        }
                    }
                    
                    setSnackbar({
                        open: true,
                        message: errorMessage,
                        severity: 'error',
                    });
                }
            }
        } catch (error) {
            console.error('Unexpected error in handleConfirmDelete:', error);
            setSnackbar({
                open: true,
                message: 'An unexpected error occurred',
                severity: 'error',
            });
        } finally {
            setIsDeleteDialogOpen(false);
            setDeleteType(null);
            setDeleteIndex(null);
            setDeleteChecklistTemplateUid(null);
        }
    };

    // ── Assignment Group Handlers ─────────────────────────────────────────────
    const handleAddAssignmentClick = () => {
        if (!canExecuteChecklist) return;
        setEditingAssignmentGroup(null);
        setCurrentView('AddAssignmentGroup');
    };

    const handleEditAssignmentClick = (group: ChecklistGroup) => {
        if (!canEditChecklist) return;
        // Resolve selected users from the current users list
        const resolvedUsers: UserOption[] = (group.userIds ?? [])
            .map((uid) => users.find((u) => String(u.id) === uid))
            .filter((u): u is UserOption => Boolean(u));

        setEditingAssignmentGroup({
            id: group.id,
            groupName: group.groupName,
            assignmentType: group.assignmentType ?? 'security_role',
            roleId: group.rolesAssignedId ? String(group.rolesAssignedId) : '',
            selectedUsers: resolvedUsers,
        });
        setCurrentView('EditAssignmentGroup');
    };

    const handleSaveAssignmentGroup = async (values: AssignmentGroupFormValues) => {
        const workplaceId = getWorkplaceId();
        const payload: Record<string, any> = {
            workplace: `/api/workplaces/${workplaceId}`,
            label: values.groupName,
            type: values.assignmentType,
            isUpdate: values.id ?? null,
        };

        if (values.assignmentType === 'security_role') {
            payload.securityRole = values.roleId
                ? `/api/security_roles/${values.roleId}`
                : null;
            payload.checklistGroupMembers = [];
        } else {
            payload.checklistGroupMembers = values.selectedUsers.map((u) => ({
                user: `/api/users/${u.id}`,
            }));
            payload.securityRole = null;
        }

        await createChecklistGroup(payload);

        setSnackbar({
            open: true,
            message: values.id
                ? 'Assignment group updated successfully'
                : 'Assignment group created successfully',
            severity: 'success',
        });
        await refreshAssignmentGroups();
        setCurrentView('ChecklistSetting');
        setEditingAssignmentGroup(null);
    };

    const handleBackFromAssignmentForm = () => {
        setCurrentView('ChecklistSetting');
        setEditingAssignmentGroup(null);
    };

    const handleDeleteAssignmentClick = async (id: number) => {
        if (!canDeleteChecklist) {
            return;
        }
        const group = assignmentGroups.find(g => g.id === id);
        if (group) {
            setAssignmentGroupToDelete(group);
            setAssignmentGroupDeleteDialogOpen(true);
        }
    };

    const handleAssignmentGroupDeleteConfirm = async () => {
        if (!assignmentGroupToDelete) return;
        
        try {
            await deleteChecklistGroup(assignmentGroupToDelete.id);
            await refreshAssignmentGroups();
            setSnackbar({
                open: true,
                message: 'Assignment group deleted successfully',
                severity: 'success',
            });
        } catch (error: any) {
            // Extract backend error message - error is the response data due to axios interceptor
            let errorMessage = 'Failed to delete assignment group';
            
            if (error && typeof error === 'object') {
                // The error object is actually the response data from the server
                if (error.error) {
                    // Direct error property from server response
                    errorMessage = error.error;
                } else if (error.message) {
                    // Generic error message
                    errorMessage = error.message;
                } else if (error.status === 409) {
                    // Fallback for 409 without specific backend message
                    errorMessage = 'Cannot delete assignment group: It is being used by existing checklist assignments';
                }
            }
            
            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error',
            });
        } finally {
            setAssignmentGroupDeleteDialogOpen(false);
            setAssignmentGroupToDelete(null);
        }
    };

    const handleAssignmentGroupDeleteCancel = () => {
        setAssignmentGroupDeleteDialogOpen(false);
        setAssignmentGroupToDelete(null);
    };

    const handleEditChecklist = (checklist: Checklist) => {
        if (!canEditChecklist) {
            return;
        }
        // Set the checklist with tasks included (tasks are already in the API response)
        setEditingChecklist(checklist);
        
        // These values will be passed to the AddChecklist component
        setTemplateValue(checklist.label);
        // Prefill radio selection based on template type
        const workplaceRadioValue = dataa?.data?.name || 'N/A';
        const templateUserPrefill = checklist.type === 'organization' ? 'Organization' : workplaceRadioValue;
        setTemplateUser(templateUserPrefill);
        // Find the category ID based on the label
        const categoryId = categorysData.find(cat => cat.label === checklist.category)?.id?.toString() || '';
        setDropdownValue(categoryId);
        // Find the frequency ID based on the label
        const frequencyId = frequencyData.find(freq => freq.label === checklist.frequency)?.id?.toString() || '';
        setFrequencyValue(frequencyId);
        setSignoffRequired(checklist.signoff_required || false);
        setCurrentView('EditChecklist');
    };

    const handleAddChecklist = () => {
        if (!canExecuteChecklist) {
            return;
        }
        setTemplateValue('');
        setCurrentView('AddChecklist');
    };

    const handleBack = () => {
        setCurrentView('ChecklistSetting');
        setEditingChecklist(null);
    };

    const handleUpdateChecklist = async (id: number, newLabel: string) => {
        if (!canEditChecklist) {
            return;
        }
        try {
            // Prepare payload for update
            const payload = {
                label: newLabel,
                // Add other fields as needed (category, frequency, templateUser, etc.)
                templateUser,
                category: { label: dropdownValue },
            };
            await updateChecklistTemplate(id, payload);
            setSnackbar({
                open: true,
                message: 'Checklist template updated successfully',
                severity: 'success',
            });
            await refreshChecklistTemplates();
            setCurrentView('ChecklistSetting');
        } catch (error) {
            setSnackbar({
                open: true,
                message: 'Failed to update checklist template',
                severity: 'error',
            });
        }
    };

    const handleSnackbarClose = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Utility function to extract user-friendly error messages
    const getErrorMessage = (error: any, defaultMessage: string): string => {
        if (!error || typeof error !== 'object') {
            return defaultMessage;
        }

        // The error object is actually the response data from the server due to axios interceptor
        if (error.error) {
            // Direct error property from server response
            return error.error;
        }

        if (error.message) {
            // Generic error message
            return error.message;
        }

        // Handle 409 Conflict errors specifically as fallback
        if (error.status === 409) {
            return defaultMessage.replace('Failed to delete', 'Cannot delete');
        }

        return defaultMessage;
    };

    const handleDropdownChange = (e: React.ChangeEvent<{ value: unknown }>) => setDropdownValue(e.target.value as string);
    const handleTemplateChange = (e: React.ChangeEvent<{ value: unknown }>) => setTemplateValue(e.target.value as string);
    const handleFrequencyChange = (e: React.ChangeEvent<{ value: unknown }>) => setFrequencyValue(e.target.value as string);
    const handleSignoffChange = (e: React.ChangeEvent<HTMLInputElement>) => setSignoffRequired(e.target.checked);
    const handleTemplateUserChange = (e: React.ChangeEvent<HTMLInputElement>) => setTemplateUser(e.target.value);
    const [frequencyValue, setFrequencyValue] = useState('');
    const [signoffRequired, setSignoffRequired] = useState(false);
    const [dropdownValue, setDropdownValue] = useState('');

    // Transform the editingChecklist into the format expected by AddChecklist
    const editValues = editingChecklist ? {
        id: editingChecklist.id,
        type: editingChecklist.type, // Add the type field from the original checklist
        workplace: {
            id: dataa?.data?.id || getWorkplaceId(),
            name: dataa?.data?.name || "Organization"
        },
        category: {
            id: parseInt(dropdownValue) || categorysData.find(cat => cat.label === editingChecklist.category)?.id || 1,
            label: categorysData.find(cat => cat.id === parseInt(dropdownValue))?.label || editingChecklist.category
        },
        frequency: {
            id: parseInt(frequencyValue) || frequencyData.find(freq => freq.label === editingChecklist.frequency)?.id || 1,
            label: frequencyData.find(freq => freq.id === parseInt(frequencyValue))?.label || editingChecklist.frequency
        },
        label: templateValue || editingChecklist.label,
        signoff_required: signoffRequired,
        template_owner: templateUser || editingChecklist.owner,
        tasks: editingChecklist.tasks || [] // Use tasks from the editingChecklist
    } : null

    // Helper to refresh checklist templates from API
    const refreshChecklistTemplates = async () => {
        try {
            setTemplatesLoading(true);
            const apiChecklists = await getChecklistTemplates();
            
            // Ensure we have an array
            const checklistsArray = Array.isArray(apiChecklists) ? apiChecklists : [];
            
            // Map API response to Checklist shape for the UI
            const mappedChecklists = checklistsArray.map((item: any) => ({
                id: item.id,
                label: item.label || item.name || 'Unnamed Template',
                category: item.category?.label || item.category?.name || '',
                owner: item.author ? `${item.author.firstName || ''} ${item.author.lastName || ''}`.trim() || 'Unknown' : 'Unknown',
                workplace: item.workplace ? { id: item.workplace.id, name: item.workplace.name } : null,
                organization: item.organization ? { id: item.organization.id, name: item.organization.name } : null,
                frequency: item.frequency?.label || item.frequency?.name || '',
                signoff_required: item.signoffRequired || false,
                tasks: item.tasks || [],
                templateUid: item.templateUid || null,
                version: item.version || 1,
                isScheduled: item.isScheduled || false,
                type: item.type || null,
            }));
            
            setChecklists(mappedChecklists);
        } catch (e) {
            console.error('Error fetching checklist templates:', e);
            setChecklists([]);
            setSnackbar({
                open: true,
                message: 'Failed to fetch checklist templates',
                severity: 'error',
            });
        } finally {
            setTemplatesLoading(false);
        }
    };

    const handleFormSubmit = async () => {
        if (!canExecuteChecklist) {
            return;
        }
        const workplaceId = dataa?.data?.id || getWorkplaceId();
        const payload = {
            workplace: { id: workplaceId },
            label: templateValue,
            category: { id: 0 },
            frequency: { id: 0 },
            templateUser,
        };
        setFormSubmitted(true);
        try {
            await createChecklistTemplete(payload);
            setSnackbar({
                open: true,
                message: 'Checklist template created successfully',
                severity: 'success',
            });
            await refreshChecklistTemplates();
            setCurrentView('ChecklistSetting');
        } catch (error) {
            setSnackbar({
                open: true,
                message: 'Failed to create checklist template',
                severity: 'error',
            });
        }
    };

    const handleCancelEdit = () => {
        setEditIndex(null);
        setEditText('');
        setShowInput(false);
        setNewCategory('');
    };

    const refreshCategories = async () => {
        try {
            setCategoriesLoading(true);
            const res = await getAllChecklistCategories();
            
            // Ensure we have an array
            const categoriesArray = Array.isArray(res) ? res : [];
            
            setCategorysData(categoriesArray);
        } catch (error) {
            console.error('Error fetching checklist categories:', error);
            setCategorysData([]);
            setSnackbar({
                open: true,
                message: 'Failed to fetch checklist categories',
                severity: 'error',
            });
        } finally {
            setCategoriesLoading(false);
        }
    };

    if (!canViewChecklist) {
        return (
            <Paper elevation={3} className={styles.containerr}>
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    You do not have permission to view checklist settings.
                </div>
            </Paper>
        );
    }

    return (
        <>
            {currentView === 'AddChecklist' && (
                (() => { return null; })()
            )}
            {currentView === 'AddChecklist' && (
                <AddChecklist
                    onBack={handleBack}
                    categorysData={categorysData}
                    categories={categories}
                    frequencyData={frequencyData}
                    dataa={dataa}
                    handleTemplateUserChange={handleTemplateUserChange}
                    handleSignoffChange={handleSignoffChange}
                    handleDropdownChange={handleDropdownChange}
                    handleTemplateChange={handleTemplateChange}
                    handleFrequencyChange={handleFrequencyChange}
                    handleFormSubmit={handleFormSubmit}
                    refreshChecklistTemplates={refreshChecklistTemplates} // <-- Pass refresh function
                />
            )}

            {currentView === 'AddAssignmentGroup' && (
                <AssignmentGroupForm
                    editValues={null}
                    onBack={handleBackFromAssignmentForm}
                    onSave={handleSaveAssignmentGroup}
                    dynamicRoles={dynamicRoles}
                    users={users}
                    usersLoading={usersLoading}
                    canEdit={canEditChecklist}
                    canExecute={canExecuteChecklist}
                />
            )}

            {currentView === 'EditAssignmentGroup' && (
                <AssignmentGroupForm
                    editValues={editingAssignmentGroup}
                    onBack={handleBackFromAssignmentForm}
                    onSave={handleSaveAssignmentGroup}
                    dynamicRoles={dynamicRoles}
                    users={users}
                    usersLoading={usersLoading}
                    canEdit={canEditChecklist}
                    canExecute={canExecuteChecklist}
                />
            )}

            {currentView === 'EditChecklist' && (
                (() => { return null; })()
            )}
            {currentView === 'EditChecklist' && (
                <AddChecklist
                    editValues={editValues}
                    onBack={handleBack}
                    categorysData={categorysData}
                    categories={categories}
                    frequencyData={frequencyData}
                    dataa={dataa}
                    handleTemplateUserChange={handleTemplateUserChange}
                    handleSignoffChange={handleSignoffChange}
                    handleDropdownChange={handleDropdownChange}
                    handleTemplateChange={handleTemplateChange}
                    handleFrequencyChange={handleFrequencyChange}
                    handleFormSubmit={handleFormSubmit}
                    refreshChecklistTemplates={refreshChecklistTemplates} // <-- Pass refresh function
                />
            )}

            {currentView === 'ChecklistSetting' && (
                <Paper elevation={3} className={styles.containerr}>
                    <div className={styles.contentWrapper}>
                        <CheckListAndToDoGroups
                            handleDeleteAssignmentClick={handleDeleteAssignmentClick}
                            assignmentGroups={assignmentGroups}
                            handleEditAssignmentClick={handleEditAssignmentClick}
                            handleAddAssignmentClick={handleAddAssignmentClick}
                            loading={assignmentGroupsLoading}
                            canEdit={canEditChecklist}
                            canDelete={canDeleteChecklist}
                            canExecute={canExecuteChecklist}
                        />

                        <ChecklistCategory
                            handleCreateClick={handleCreateClick}
                            showInput={showInput}
                            newCategory={newCategory}
                            setNewCategory={setNewCategory}
                            categorysData={categorysData}
                            handleAddCategory={handleAddCategory}
                            editIndex={editIndex}
                            setEditText={setEditText}
                            editText={editText}
                            handleSaveEdit={handleSaveEdit}
                            handleEditClick={handleEditClick}
                            handleDeleteCategoryClick={handleDeleteCategoryClick}
                            handleCancelEdit={handleCancelEdit}
                            loading={categoriesLoading}
                            canEdit={canEditChecklist}
                            canDelete={canDeleteChecklist}
                            canExecute={canExecuteChecklist}
                        />

                        <ChecklistTemplates
                            sortedChecklists={sortedChecklists}
                            handleAddChecklist={handleAddChecklist}
                            handleEditChecklist={handleEditChecklist}
                            handleDeleteChecklistClick={handleDeleteChecklistClick}
                            loading={templatesLoading}
                            canEdit={canEditChecklist}
                            canDelete={canDeleteChecklist}
                            canExecute={canExecuteChecklist}
                        />

                    </div>

                                         <DeleteDialog
                         open={isDeleteDialogOpen}
                         onOpenChange={setIsDeleteDialogOpen}
                         deleteType={deleteType}
                         deleteIndex={deleteIndex}
                         deleteChecklistTemplateUid={deleteChecklistTemplateUid}
                         categorysData={categorysData}
                         checklists={checklists}
                         setDeleteType={setDeleteType}
                         setDeleteIndex={setDeleteIndex}
                         setDeleteChecklistTemplateUid={setDeleteChecklistTemplateUid}
                         handleConfirmDelete={handleConfirmDelete}
                     />

                     {/* Assignment Group Delete Confirmation Dialog */}
                     <Dialog
                         open={assignmentGroupDeleteDialogOpen}
                         onClose={handleAssignmentGroupDeleteCancel}
                         aria-labelledby="assignment-group-delete-dialog-title"
                         aria-describedby="assignment-group-delete-dialog-description"
                         maxWidth="sm"
                         fullWidth
                         style={{ zIndex: 9999 }}
                         PaperProps={{
                             style: {
                                 backgroundColor: 'white',
                                 boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                 borderRadius: '8px',
                                 minWidth: '400px'
                             }
                         }}
                     >
                         <DialogTitle id="assignment-group-delete-dialog-title">
                             Confirm Delete
                         </DialogTitle>
                         <DialogContent>
                             <DialogContentText id="assignment-group-delete-dialog-description">
                                 Are you sure you want to delete the assignment group &ldquo;{assignmentGroupToDelete?.groupName}&rdquo;? This action cannot be undone.
                             </DialogContentText>
                         </DialogContent>
                         <DialogActions>
                             <Button onClick={handleAssignmentGroupDeleteCancel} style={{ color: 'black' }}>
                                 Cancel
                             </Button>
                             <Button 
                                 onClick={handleAssignmentGroupDeleteConfirm} 
                                 style={{ backgroundColor: 'red', color: 'white' }}
                             >
                                 Delete
                             </Button>
                         </DialogActions>
                     </Dialog>

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={6000}
                        onClose={handleSnackbarClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        <Alert
                            onClose={handleSnackbarClose}
                            severity={snackbar.severity}
                            variant="filled"
                            className={styles.snackbar}
                        >
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </Paper>
            )}
        </>
    );
};

export default ChecklistSetting;